import SessionState from "../stateTypes/sessionState";
import ConfigurationState from "../stateTypes/configurationState";
import ErrorState from "../stateTypes/errorState";
import MenuState from "../stateTypes/menuState";
import ViewState from "../stateTypes/viewState";
import { viewIndexToFrame, viewIndexToSeries, viewIndexToSeriesId } from "../../utilities/viewUtils";

export const getSessionId = ({ session }: { session: SessionState }) => session.sessionData?.sessionId;
export const getAppConfig = ({ session }: { session: SessionState }) => session.appConfig;
export const getIsLoading = ({ session }: { session: SessionState }) => session.isLoading;
export const getFrontendSettings = ({ session }: { session: SessionState }) => session.frontendSettings;
export const getLanguage = ({ session }: { session: SessionState }) => session.language;
export const getShowPrice = ({ session }: { session: SessionState }) => session.showPrice;
export const getApproximatePrices = ({ session }: { session: SessionState }) => session.approximatePrices;
export const getShowCancelButton = ({ session }: { session: SessionState }) => session.showCancelButton;
export const getComposedUrl = ({ session }: { session: SessionState }) => session.sessionData?.composedUrl;
export const getDealerAccount = ({ session }: { session: SessionState }) => session.dealerAccount;
export const getSummaryData = ({ session }: { session: SessionState }) => session.summaryData;
export const getCurrency = ({ session }: { session: SessionState }) => session.sessionData?.currency;
export const getCurrencyForAnalytics = ({ session }: { session: SessionState }) =>
    session.sessionData?.currencyForAnalytics;
export const getCurrencyIsPrefixed = ({ session }: { session: SessionState }) =>
    session.sessionData?.currencyIsPrefixed;

export const getConfigurationString = ({ configuration }: { configuration: ConfigurationState }) =>
    configuration.configurationString;
export const getPreviewConfig = ({ configuration }: { configuration: ConfigurationState }) =>
    configuration.previewConfig;
export const getImageUrl = ({ configuration }: { configuration: ConfigurationState }) => configuration.imageUrl;
export const getSelectedMenu = ({ configuration }: { configuration: ConfigurationState }) => configuration.selectedMenu;
export const getMenuStructure = ({ configuration }: { configuration: ConfigurationState }) =>
    configuration.menuStructure;
export const getMenuItems = ({ configuration }: { configuration: ConfigurationState }) => configuration.menuItems;
export const getItemMap = ({ configuration }: { configuration: ConfigurationState }) => configuration.itemMap;
export const getItemStates = ({ configuration }: { configuration: ConfigurationState }) => configuration.itemStates;
export const getPreviewItem = ({ configuration }: { configuration: ConfigurationState }) => configuration.previewItem;
export const getSummaryItems = ({ configuration }: { configuration: ConfigurationState }) => configuration.summaryItems;
export const getModelId = ({ configuration }: { configuration: ConfigurationState }) => configuration.modelId;
export const getTotalPrice = ({
    session,
    configuration,
}: {
    session: SessionState;
    configuration: ConfigurationState;
}) => {
    const { sessionData } = session;
    if (!sessionData) return 0;

    // Calculate total price here since the frontend has more items
    // in the summary than there are in the backend configuration.
    const totalPrice = configuration.summaryItems.reduce((sum, s) => {
        const price = configuration.itemStates.getPrice(s.item.id);
        return sum + (price ? price.priceValue * s.amount : 0);
    }, 0);

    return totalPrice;
};

export const getAllImageSeries = ({ view }: { view: ViewState }) => view.imageSeries;
export const getCurrentImageSeries = ({ view }: { view: ViewState }) =>
    view.activeViewIndex !== null ? viewIndexToSeries(view.imageSeries, view.activeViewIndex) : null;
export const getActiveImageSeriesId = ({ view }: { view: ViewState }) =>
    view.activeViewIndex !== null ? viewIndexToSeriesId(view.imageSeries, view.activeViewIndex) : null;
export const getActiveViewIndex = ({ view }: { view: ViewState }) => view.activeViewIndex;
export const getMaxViewIndex = ({ view }: { view: ViewState }) =>
    view.imageSeries.reduce((sum, s) => sum + s.frames.length, 0) - 1;
export const getActiveFrame = ({ view }: { view: ViewState }) =>
    view.activeViewIndex !== null ? viewIndexToFrame(view.imageSeries, view.activeViewIndex) : null;

export const getSideMenuOpen = ({ menu }: { menu: MenuState }) => menu.sideMenuOpen;
export const getShowMobileSummary = ({ menu }: { menu: MenuState }) => menu.showMobileSummary;

export const getError = ({ error }: { error: ErrorState }) => error.error;
